/**
 * Scheduler Singleton - module-level state that survives React unmounts.
 *
 * Manages the local browser `setInterval` scheduler for recurring benchmarks.
 * Persists scheduler metadata to localStorage so the UI can reflect status
 * across page navigations within the same session.
 */

import { runBenchmark } from './benchmark-runner'
import { saveResults } from './api-client'
import type { BenchmarkProgress } from '@/types'

// ── Interfaces ────────────────────────────────────────────────────────

export interface SchedulerPersistedState {
  running: boolean
  intervalMs: number
  totalRuns: number
  lastRunTime: string | null
  startedAt: string | null
}

export interface SchedulerRuntimeState {
  intervalId: ReturnType<typeof setInterval> | null
  abortController: AbortController | null
  currentlyRunning: boolean
  progress: BenchmarkProgress | null
}

// ── TrendPoint ───────────────────────────────────────────────────────

export interface TrendPoint {
  runId: string
  timestamp: string
  scheduled: boolean
  profileName: string
  profileType: string
  avgTotalSec: number
  avgTtftSec: number
  avgThroughput: number
  p95TotalSec: number
  callCount: number
  successCount: number
}

// ── Constants ─────────────────────────────────────────────────────────

const SCHEDULER_STATE_KEY = 'bedrock-diag-scheduler-state'
const TREND_RESULTS_KEY = 'bedrock-diag-trend-results'

// ── Module-level variables (survive React unmounts) ───────────────────

let intervalId: ReturnType<typeof setInterval> | null = null
let abortController: AbortController | null = null
let currentlyRunning = false
let progress: BenchmarkProgress | null = null

// ── localStorage helpers ──────────────────────────────────────────────

function loadPersistedState(): SchedulerPersistedState {
  try {
    const raw = localStorage.getItem(SCHEDULER_STATE_KEY)
    if (raw) return JSON.parse(raw) as SchedulerPersistedState
  } catch { /* ignore corrupt data */ }
  return {
    running: false,
    intervalMs: 0,
    totalRuns: 0,
    lastRunTime: null,
    startedAt: null,
  }
}

function savePersistedState(state: SchedulerPersistedState): void {
  try {
    localStorage.setItem(SCHEDULER_STATE_KEY, JSON.stringify(state))
  } catch (err) {
    console.warn('[scheduler-singleton] Failed to persist state:', err)
  }
}

// ── Trend data helpers ────────────────────────────────────────────────

function loadTrendResults(): TrendPoint[] {
  try {
    const raw = localStorage.getItem(TREND_RESULTS_KEY)
    if (raw) return JSON.parse(raw) as TrendPoint[]
  } catch { /* ignore corrupt data */ }
  return []
}

function saveTrendResults(points: TrendPoint[]): void {
  try {
    localStorage.setItem(TREND_RESULTS_KEY, JSON.stringify(points))
  } catch (err) {
    console.warn('[scheduler-singleton] Failed to save trend results:', err)
  }
}

// ── Retention Policy ──────────────────────────────────────────────────

/**
 * Filters out points older than 30 days and keeps at most 1000 most recent.
 */
export function applyRetentionPolicy(points: TrendPoint[]): TrendPoint[] {
  const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
  const recent = points.filter(p => new Date(p.timestamp).getTime() >= thirtyDaysAgo)
  if (recent.length > 1000) {
    return recent
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, 1000)
  }
  return recent
}

// ── Core scheduler functions ──────────────────────────────────────────

/**
 * Run a single benchmark iteration as part of the scheduler.
 * Updates module-level state and persists results.
 */
async function runScheduledBenchmark(): Promise<void> {
  // Dynamically import the store to avoid circular dependencies
  const { useAppStore } = await import('@/store/app-store')
  const state = useAppStore.getState()

  const profiles = [
    ...state.profiles.filter(p => p.enabled),
    ...state.presets.filter(p => p.enabled),
  ]
  if (profiles.length === 0) return

  const ac = new AbortController()
  abortController = ac
  currentlyRunning = true
  progress = null

  try {
    const run = await runBenchmark({
      profiles,
      iterations: 1,
      signal: ac.signal,
      onProgress: (p) => { progress = p },
    })

    // Save results (IndexedDB + optional cloud sync)
    await saveResults(run)

    // Convert run stats to trend points and persist
    const newPoints: TrendPoint[] = run.stats.map(s => ({
      runId: run.id,
      timestamp: run.endTime,
      scheduled: true,
      profileName: s.profileName,
      profileType: s.profileType,
      avgTotalSec: s.avgTotalSec,
      avgTtftSec: s.avgTtftSec,
      avgThroughput: s.avgThroughput,
      p95TotalSec: s.p95TotalSec,
      callCount: s.callCount,
      successCount: s.successCount,
    }))

    const existing = loadTrendResults()
    const updated = applyRetentionPolicy([...existing, ...newPoints])
    saveTrendResults(updated)

    // Update persisted scheduler state
    const persisted = loadPersistedState()
    savePersistedState({
      ...persisted,
      totalRuns: persisted.totalRuns + 1,
      lastRunTime: new Date().toISOString(),
    })
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      console.error('[scheduler-singleton] Scheduled benchmark failed:', err)
    }
    // Don't stop the scheduler for transient failures - just log and continue
  } finally {
    abortController = null
    currentlyRunning = false
    progress = null
  }
}

/**
 * Start the local scheduler. Runs the first benchmark immediately,
 * then repeats at the given interval.
 */
export function startScheduler(intervalMs: number): void {
  if (intervalId !== null) return // already running

  savePersistedState({
    running: true,
    intervalMs,
    totalRuns: 0,
    lastRunTime: null,
    startedAt: new Date().toISOString(),
  })

  // Run first benchmark immediately
  runScheduledBenchmark()

  // Schedule subsequent runs
  intervalId = setInterval(() => {
    // Only start a new run if the previous one finished
    if (!currentlyRunning) {
      runScheduledBenchmark()
    }
  }, intervalMs)
}

/**
 * Stop the local scheduler. Clears the interval, aborts any in-progress
 * run, and resets persisted state to stopped.
 */
export function stopScheduler(): void {
  if (intervalId !== null) {
    clearInterval(intervalId)
    intervalId = null
  }

  if (abortController) {
    abortController.abort()
    abortController = null
  }

  currentlyRunning = false
  progress = null

  const persisted = loadPersistedState()
  savePersistedState({
    ...persisted,
    running: false,
  })
}

/**
 * Returns the merged persisted + runtime scheduler state.
 */
export function getSchedulerState(): SchedulerPersistedState & SchedulerRuntimeState {
  const persisted = loadPersistedState()
  return {
    ...persisted,
    intervalId,
    abortController,
    currentlyRunning,
    progress,
  }
}

/**
 * Returns whether the scheduler is currently running (has an active interval).
 */
export function isSchedulerRunning(): boolean {
  return intervalId !== null
}

/**
 * Clean up stale scheduler state. If localStorage says `running: true`
 * but no active `setInterval` exists (e.g., after a page refresh),
 * reset persisted state to `running: false`.
 */
export function cleanupStaleState(): void {
  const persisted = loadPersistedState()
  if (persisted.running && intervalId === null) {
    savePersistedState({
      ...persisted,
      running: false,
    })
  }
}
